import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Sparkles, X, Copy, Check } from "lucide-react";
import { useAuth, API } from "@/auth/AuthContext";

const STORAGE_KEY = "cmp_popup_state_v1";
const DELAY_MS = 20_000;
const SHOW_AGAIN_DAYS = 30;

function readState() {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    } catch {
        return {};
    }
}
function writeState(p) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
    } catch {
        /* ignore */
    }
}

export default function WelcomePopup() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [view, setView] = useState("offer"); // 'offer' | 'success'
    const [email, setEmail] = useState("");
    const [hp, setHp] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [code, setCode] = useState("");
    const [copied, setCopied] = useState(false);
    const fired = useRef(false);

    // Decide if we should ever show
    useEffect(() => {
        if (user) return; // signed-in users skip
        const s = readState();
        if (s.dismissedUntil && Date.now() < s.dismissedUntil) return;
        if (s.subscribedAt) return; // never bug subscribers again

        const triggerShow = () => {
            if (fired.current) return;
            fired.current = true;
            setOpen(true);
        };

        const timer = setTimeout(triggerShow, DELAY_MS);
        const onMouseOut = (e) => {
            if (e.clientY <= 0 && !e.relatedTarget) triggerShow();
        };
        // Mobile exit-intent fallback: trigger on scroll-up after some engagement
        let lastY = window.scrollY;
        let upDist = 0;
        const onScroll = () => {
            const y = window.scrollY;
            if (y < lastY) upDist += lastY - y;
            else upDist = 0;
            lastY = y;
            if (upDist > 250 && window.scrollY > 300) triggerShow();
        };
        document.addEventListener("mouseout", onMouseOut);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            clearTimeout(timer);
            document.removeEventListener("mouseout", onMouseOut);
            window.removeEventListener("scroll", onScroll);
        };
    }, [user]);

    // Lock body scroll when open
    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = "";
            };
        }
    }, [open]);

    const dismiss = (longSnooze = false) => {
        const until = Date.now() + (longSnooze ? SHOW_AGAIN_DAYS : 1) * 86400_000;
        writeState({ ...readState(), dismissedUntil: until });
        setOpen(false);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setErr("");
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            setErr("Enter a valid email address.");
            return;
        }
        setBusy(true);
        try {
            const res = await axios.post(`${API}/marketing/subscribe`, {
                email: email.trim(),
                source: "popup",
                website: hp, // honeypot
            });
            setCode(res.data.code);
            writeState({
                ...readState(),
                subscribedAt: Date.now(),
                subscribedEmail: email.trim().toLowerCase(),
                welcomeCode: res.data.code,
            });
            setView("success");
        } catch (ex) {
            setErr(
                ex?.response?.data?.detail ||
                    "Couldn't subscribe right now. Try again in a moment.",
            );
        } finally {
            setBusy(false);
        }
    };

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            /* ignore */
        }
    };

    if (!open) return null;

    return (
        <div
            data-testid="welcome-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="welcome-popup-title"
            className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-up"
            onClick={() => dismiss(false)}
        >
            <div
                className="relative w-full max-w-md bg-cream rounded-[22px] overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    data-testid="welcome-popup-close"
                    onClick={() => dismiss(true)}
                    aria-label="Close"
                    className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
                >
                    <X size={18} />
                </button>

                {view === "offer" ? (
                    <>
                        <div className="bg-forest grain text-white px-6 pt-7 pb-6 text-center">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold/20 border border-gold/40 text-gold text-[10px] tracking-[0.3em] font-bold uppercase">
                                <Sparkles size={11} /> First-order offer
                            </span>
                            <h2
                                id="welcome-popup-title"
                                className="mt-4 font-serif font-black text-3xl sm:text-[34px] leading-tight"
                            >
                                10% off your first order
                            </h2>
                            <p className="mt-2 text-mint text-sm leading-relaxed">
                                Plus stack it with{" "}
                                <span className="text-gold font-bold">
                                    up to 20% volume off
                                </span>{" "}
                                and our{" "}
                                <span className="text-gold font-bold">
                                    10% subscriber bonus
                                </span>{" "}
                                — that's up to{" "}
                                <span className="text-gold font-bold">
                                    40% off
                                </span>{" "}
                                your first box.
                            </p>
                        </div>

                        <div className="px-6 py-6">
                            <ul className="text-sm text-navy/85 space-y-2 mb-5">
                                <Bullet>
                                    Authentic Caribbean meals, hand-made weekly
                                </Bullet>
                                <Bullet>
                                    Delivered Fridays &amp; Saturdays across
                                    Greater Boston
                                </Bullet>
                                <Bullet>
                                    First-time discounts stack at checkout
                                </Bullet>
                            </ul>

                            <form onSubmit={submit} noValidate>
                                {/* Honeypot — invisible to humans */}
                                <input
                                    type="text"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={hp}
                                    onChange={(e) => setHp(e.target.value)}
                                    name="website"
                                    style={{
                                        position: "absolute",
                                        left: "-9999px",
                                        width: 1,
                                        height: 1,
                                        opacity: 0,
                                    }}
                                    aria-hidden="true"
                                />
                                <label className="block">
                                    <span className="text-[11px] uppercase tracking-widest font-bold text-muted2">
                                        Email address
                                    </span>
                                    <input
                                        data-testid="welcome-popup-email"
                                        type="email"
                                        required
                                        autoFocus
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="mt-1.5 w-full px-4 py-3 rounded-xl bg-white border border-mint focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none transition"
                                    />
                                </label>
                                {err && (
                                    <p
                                        data-testid="welcome-popup-error"
                                        className="mt-2 text-sm text-burnt"
                                    >
                                        {err}
                                    </p>
                                )}
                                <button
                                    type="submit"
                                    data-testid="welcome-popup-submit"
                                    disabled={busy}
                                    className="mt-4 w-full rounded-full bg-gold hover:bg-goldDark text-navy font-bold py-3.5 transition-all active:scale-[0.98] shadow-[0_10px_28px_rgba(244,164,53,0.45)] disabled:opacity-60"
                                >
                                    {busy
                                        ? "Sending your code…"
                                        : "Send my 10% code"}
                                </button>
                                <p className="mt-3 text-[11px] text-muted2 text-center leading-relaxed">
                                    We'll never share your email. Unsubscribe
                                    anytime.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => dismiss(true)}
                                    className="mt-2 w-full text-[12px] text-muted2 hover:text-navy underline underline-offset-2"
                                >
                                    No thanks, I'll pay full price
                                </button>
                            </form>
                        </div>
                    </>
                ) : (
                    <div className="px-6 py-8 text-center">
                        <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-forest text-white">
                            <Check size={28} strokeWidth={3} />
                        </div>
                        <h3 className="mt-4 font-serif font-black text-2xl text-navy">
                            You're in!
                        </h3>
                        <p className="mt-2 text-sm text-navy/80 leading-relaxed">
                            We just emailed your code. Use it at checkout — and
                            stack it with our volume + subscriber discounts.
                        </p>
                        <div className="mt-5 mx-auto inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-white border-2 border-dashed border-gold">
                            <span className="font-mono text-2xl tracking-[6px] font-black text-forest">
                                {code}
                            </span>
                            <button
                                type="button"
                                data-testid="welcome-popup-copy"
                                onClick={copyCode}
                                className="text-forest hover:text-burnt transition-colors"
                                aria-label="Copy code"
                            >
                                {copied ? (
                                    <Check size={18} />
                                ) : (
                                    <Copy size={18} />
                                )}
                            </button>
                        </div>
                        <a
                            href="/menu"
                            data-testid="welcome-popup-build-box"
                            onClick={() => setOpen(false)}
                            className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gold hover:bg-goldDark text-navy font-bold px-7 py-3.5 transition-all active:scale-95 shadow-[0_10px_28px_rgba(244,164,53,0.45)]"
                        >
                            Build my box →
                        </a>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="mt-3 block mx-auto text-[12px] text-muted2 hover:text-navy underline"
                        >
                            I'll come back later
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function Bullet({ children }) {
    return (
        <li className="flex items-start gap-2">
            <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full bg-mint">
                <Check size={10} className="text-forest" strokeWidth={3} />
            </span>
            <span>{children}</span>
        </li>
    );
}
